import { type ReactElement } from 'react';
import ReactSelect, { components, type SingleValue, type StylesConfig } from 'react-select';
import { Icon } from '@utmb/design-system/icons/Icon';
import { Font } from '@utmb/design-system/text/font';
import { IconsEnum } from '@utmb/types/icons';
import styles from './filter-select.module.scss';

export type FilterSelectValue = string | number | null;

export interface FilterSelectOption<TValue extends FilterSelectValue = FilterSelectValue> {
    value: TValue;
    label: string;
    isContinent?: boolean;
}

export interface FilterSelectProps<TValue extends FilterSelectValue = FilterSelectValue> {
    label?: string;
    placeholder?: string;
    options: FilterSelectOption<TValue>[];
    value: FilterSelectOption<TValue> | null;
    onChange: (option: SingleValue<FilterSelectOption<TValue>>) => void;
    isDisabled?: boolean;
    isLoading?: boolean;
    isClearable?: boolean;
    optionComponent?: typeof components.Option;
}

const DropdownIndicator = (props: any): ReactElement => (
    <components.DropdownIndicator {...props}>
        <Icon variant={IconsEnum.CHEVRON_DOWN} />
    </components.DropdownIndicator>
);

export function FilterSelect<TValue extends FilterSelectValue = FilterSelectValue>({
    label,
    placeholder,
    options,
    value,
    onChange,
    isDisabled = false,
    isLoading = false,
    isClearable = true,
    optionComponent,
}: FilterSelectProps<TValue>): ReactElement {
    const customStyles: StylesConfig<FilterSelectOption<TValue>> = {
        container: (base, { isDisabled: disabled }) => ({
            ...base,
            opacity: disabled ? 0.25 : 1,
        }),
        option: (base, { isFocused, isSelected, data }) => ({
            ...base,
            cursor: 'pointer',
            color: 'var(--utmb-color-white)',
            backgroundColor: isFocused || isSelected ? 'color-mix(in srgb, var(--utmb-color-white) 20%, transparent)' : 'transparent',
            fontFamily: data.isContinent ? 'var(--utmb-font-futura), sans-serif' : 'var(--utmb-font-futura-book), sans-serif',
            fontWeight: data.isContinent ? 500 : 'normal',
            fontSize: '16px',
            textTransform: data.isContinent ? 'uppercase' : 'none',
        }),
        input: (base) => ({
            ...base,
            color: 'var(--utmb-color-white)',
            fontFamily: 'var(--utmb-font-futura-book), sans-serif',
            fontWeight: 'normal',
            fontSize: '16px',
        }),
        noOptionsMessage: (base) => ({
            ...base,
            color: 'color-mix(in srgb, var(--utmb-color-white) 70%, transparent)',
            fontFamily: 'var(--utmb-font-futura-book), sans-serif',
            fontWeight: 'normal',
            fontSize: '16px',
        }),
        control: (base, { menuIsOpen }) => ({
            ...base,
            cursor: 'pointer',
            boxShadow: 'none',
            minHeight: '50px',
            background: 'color-mix(in srgb, var(--utmb-color-white) 10%, transparent)',
            border: '1px solid color-mix(in srgb, var(--utmb-color-white) 20%, transparent)',
            borderRadius: 'var(--utmb-border-radius)',
            color: 'var(--utmb-color-white)',
            ...(menuIsOpen
                ? {
                      borderBottomColor: 'transparent',
                      borderBottomLeftRadius: 0,
                      borderBottomRightRadius: 0,
                  }
                : {}),
            '&:hover': {
                border: '1px solid var(--utmb-color-white)',
                ...(menuIsOpen
                    ? {
                          borderBottomColor: 'transparent',
                          borderBottomLeftRadius: 0,
                          borderBottomRightRadius: 0,
                      }
                    : {}),
                '& .filter-select__dropdown-indicator': {
                    color: 'var(--utmb-color-white)',
                },
            },
            '&:focus-within': {
                border: '1px solid var(--utmb-color-white) !important',
                ...(menuIsOpen
                    ? {
                          borderBottomColor: 'transparent !important',
                          borderBottomLeftRadius: '0 !important',
                          borderBottomRightRadius: '0 !important',
                      }
                    : {}),
            },
        }),
        singleValue: (base) => ({
            ...base,
            color: 'var(--utmb-color-white)',
            fontFamily: 'var(--utmb-font-futura-book), sans-serif',
            fontWeight: 'normal',
            fontSize: '16px',
        }),
        placeholder: (base) => ({
            ...base,
            color: 'color-mix(in srgb, var(--utmb-color-white) 70%, transparent)',
            fontFamily: 'var(--utmb-font-futura-book), sans-serif',
            fontWeight: 'normal',
            fontSize: '16px',
        }),
        dropdownIndicator: (base, { selectProps }) => ({
            ...base,
            color: 'color-mix(in srgb, var(--utmb-color-white) 20%, transparent)',
            transform: `rotate(${selectProps.menuIsOpen ? -180 : 0}deg)`,
            transitionDuration: '0.2s',
            transitionProperty: 'transform',
            '&:hover': {
                color: 'color-mix(in srgb, var(--utmb-color-white) 20%, transparent)',
            },
        }),
        indicatorSeparator: (base) => ({
            ...base,
            backgroundColor: 'color-mix(in srgb, var(--utmb-color-white) 20%, transparent)',
        }),
        clearIndicator: (base) => ({
            ...base,
            color: 'color-mix(in srgb, var(--utmb-color-white) 20%, transparent)',
            cursor: 'pointer',
            '&:hover': {
                color: 'var(--utmb-color-white)',
            },
        }),
        menu: (base) => ({
            ...base,
            marginTop: 0,
            background: 'color-mix(in srgb, var(--utmb-color-white) 10%, transparent)',
            backdropFilter: 'blur(14px)',
            WebkitBackdropFilter: 'blur(14px)',
            border: '1px solid var(--utmb-color-white)',
            borderTop: 'none',
            borderTopLeftRadius: 0,
            borderTopRightRadius: 0,
            borderBottomLeftRadius: 'var(--utmb-border-radius)',
            borderBottomRightRadius: 'var(--utmb-border-radius)',
            overflow: 'hidden',
            boxShadow: 'none',
            filter: 'none',
        }),
        menuList: (base) => ({
            ...base,
            paddingTop: 0,
            paddingBottom: 0,
        }),
    };

    const componentsConfig: Record<string, unknown> = { DropdownIndicator };
    if (optionComponent) componentsConfig.Option = optionComponent;

    return (
        <div className={styles.filterSelect}>
            {label && (
                <Font.OxaniumRegular mobile="12" desktop="14" className={styles.label}>
                    {label}
                </Font.OxaniumRegular>
            )}
            <ReactSelect
                className={styles.reactSelect}
                classNamePrefix="filter-select"
                components={componentsConfig}
                styles={customStyles as StylesConfig}
                placeholder={placeholder}
                options={options}
                onChange={(opt) => onChange(opt as SingleValue<FilterSelectOption<TValue>>)}
                value={value}
                isClearable={isClearable}
                isDisabled={isDisabled}
                isLoading={isLoading}
            />
        </div>
    );
}
